// Versão de celular (B29, M3): o escritório vira um jogo na tela estreita.
//
// Monta só quando a tela é de celular (ver MOBILE_QUERY): um HUD fino no topo, uma barra de abas
// embaixo e os painéis como bottom sheets (alça, arrastar, meia e altura cheia). Fora disso, nada
// deste módulo existe no DOM e o desktop segue como estava. Os painéis não são reescritos: este
// módulo só lhes dá a alça, a altura e o fundo, e usa os botões de fechar que eles já têm.
//
// As funções puras (estado das sheets, anel de consumo, grade de pixel) são testadas em
// tests/mobile.test.ts sem DOM.

import type { OfficeSnapshot } from '../core/types.js';
import { type Chave, getLang, IDIOMAS, onLangChange, t, tActivity } from './i18n.js';
import { ferramentaDaBarra, ouvirBarra, refreshTools, textoSelo } from './toolbar.js';
import { montaPainelPush, type TextosPush } from './push.js';
import { painelSons } from './sound.js';
import { consumoDoAnel } from './stats.js';
import type { Bateria } from './desempenho.js';

/** Estados de uma bottom sheet aberta. A fechada não tem estado: não está no mapa. */
export type Folha = 'meia' | 'cheia' | 'fechada';

/** Alturas em px de meia e de altura cheia (a fechada é zero). */
export interface Medidas {
  meia: number;
  cheia: number;
}

const ORDEM: readonly Folha[] = ['fechada', 'meia', 'cheia'];
/** Velocidade (px por ms) a partir da qual o gesto vale como "toque rápido" e anda um estado só. */
const VELOCIDADE_FLICK = 0.45;

/** Altura de uma sheet no estado dado. */
export function alturaDaFolha(estado: Folha, medidas: Medidas): number {
  if (estado === 'meia') return medidas.meia;
  if (estado === 'cheia') return medidas.cheia;
  return 0;
}

/**
 * Estado da sheet quando a pessoa solta a alça. `dy` é o deslocamento do dedo em px (positivo
 * para baixo); `vel` é a velocidade (positiva para baixo). Gesto rápido anda um estado na direção
 * do gesto; devagar, vai para o estado de altura mais perto da altura onde o dedo parou.
 */
export function estadoAposArrasto(atual: Folha, dy: number, vel: number, medidas: Medidas): Folha {
  const posicao = ORDEM.indexOf(atual);
  if (Math.abs(vel) >= VELOCIDADE_FLICK) {
    const passo = vel > 0 ? -1 : 1;
    return ORDEM[Math.min(ORDEM.length - 1, Math.max(0, posicao + passo))] ?? atual;
  }
  const alvo = alturaDaFolha(atual, medidas) - dy;
  let melhor: Folha = atual;
  let distancia = Infinity;
  for (const estado of ORDEM) {
    const d = Math.abs(alturaDaFolha(estado, medidas) - alvo);
    if (d < distancia) {
      distancia = d;
      melhor = estado;
    }
  }
  return melhor;
}

/** Maior percentual da janela de 5 h entre as CLIs (0 a 100), ou undefined se nenhuma informa. */
export function maiorConsumo5h(provedores: ReadonlyArray<{ usage?: { windows: ReadonlyArray<{ label: string; percent: number }> } }>): number | undefined {
  let maior: number | undefined;
  for (const p of provedores) {
    for (const w of p.usage?.windows ?? []) {
      if (w.label !== '5h') continue;
      const pct = Math.round(Math.max(0, Math.min(100, w.percent)));
      if (maior === undefined || pct > maior) maior = pct;
    }
  }
  return maior;
}

/** Nível do consumo, com os mesmos cortes da barra de consumo do desktop (70 e 90). */
export function nivelConsumo(pct: number): 'baixo' | 'medio' | 'alto' {
  if (pct >= 90) return 'alto';
  if (pct >= 70) return 'medio';
  return 'baixo';
}

/**
 * Desenha uma grade de pixels ('#' é pixel cheio, qualquer outro caractere é vazio) como SVG:
 * um retângulo por trecho contínuo de cada linha, sem anti-aliasing, pintado com currentColor.
 */
export function pixelSvg(grade: readonly string[]): string {
  const colunas = Math.max(0, ...grade.map((linha) => linha.length));
  const retangulos: string[] = [];
  grade.forEach((linha, y) => {
    let x = 0;
    while (x < linha.length) {
      if (linha[x] !== '#') {
        x++;
        continue;
      }
      const inicio = x;
      while (x < linha.length && linha[x] === '#') x++;
      retangulos.push(`<rect x="${inicio}" y="${y}" width="${x - inicio}" height="1"/>`);
    }
  });
  return `<svg viewBox="0 0 ${colunas} ${grade.length}" aria-hidden="true" focusable="false" shape-rendering="crispEdges" fill="currentColor">${retangulos.join('')}</svg>`;
}

// ------------------------------------------------------------------ montagem (só em tela de celular)

/** Quando a versão de celular vale: tela estreita, ou celular deitado (pouca altura, toque). Igual à @media do style.css. */
export const MOBILE_QUERY = '(max-width: 760px), (pointer: coarse) and (max-height: 500px)';

/** A versão de celular está valendo agora? (fora do navegador, sem janela, é falso.) */
export function ehCelular(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * Fila do modal de pedido no celular: os pedidos pendentes, na ordem em que chegaram, sem os que
 * a pessoa já tirou da frente. Mostra sempre o primeiro; ao resolver um, o próximo assume (1 de N).
 */
export function filaDePedidos<T extends { id: string }>(pedidos: readonly T[], escondidos: ReadonlySet<string>): { atual?: T; posicao: number; total: number } {
  const visiveis = pedidos.filter((p) => !escondidos.has(p.id));
  return { atual: visiveis[0], posicao: visiveis.length ? 1 : 0, total: visiveis.length };
}

/**
 * Vibração leve quando alguém precisa de você. O navegador só deixa vibrar depois que a pessoa
 * tocou na página, e nem todo aparelho tem vibração; sem um dos dois, não faz nada.
 */
export function vibraPedido(nav: Partial<Navigator> | undefined = globalThis.navigator): boolean {
  if (typeof nav?.vibrate !== 'function' || !nav.userActivation?.hasBeenActive) return false;
  return nav.vibrate([80, 40, 80]);
}

/** O que a Battery API entrega (só o que o campus usa). */
interface BateriaDoNavegador {
  level: number;
  charging: boolean;
  addEventListener(tipo: 'levelchange' | 'chargingchange', ouvinte: () => void): void;
}

/**
 * Acompanha a bateria (B29-M6) para as telas paradas: `ao` recebe a leitura agora e a cada mudança.
 * A Battery API existe no Chrome do Android e não existe no Safari do iPhone; sem ela, nada acontece.
 */
export async function observaBateria(ao: (bateria: Bateria) => void, nav: Partial<Navigator & { getBattery: () => Promise<BateriaDoNavegador> }> | undefined = globalThis.navigator): Promise<void> {
  if (typeof nav?.getBattery !== 'function') return;
  const bateria = await nav.getBattery();
  const leia = (): void => ao({ nivel: bateria.level, carregando: bateria.charging });
  bateria.addEventListener('levelchange', leia);
  bateria.addEventListener('chargingchange', leia);
  leia();
}

/** Meia altura da sheet, em fração da tela. */
const FRACAO_MEIA = 0.5;

type AbaId = 'escritorio' | 'agentes' | 'entregas' | 'dia' | 'mais';

/** Ícones das abas: grade de 10x10 pixels ('#' é pixel cheio). */
const GRADE: Record<AbaId, readonly string[]> = {
  escritorio: ['..........', '..........', '..######..', '..#.##.#..', '..######..', '..#.##.#..', '..######..', '..#.##.#..', '..##..##..', '##########'],
  agentes: ['..........', '...####...', '..######..', '..######..', '...####...', '..########', '.########.', '##########', '##########', '##########'],
  entregas: ['##########', '##########', '####..####', '####..####', '##########', '##########', '##########', '##########', '##########', '##########'],
  dia: ['........##', '........##', '........##', '....##.##.', '....##.##.', '....##.##.', '.##.##.##.', '.##.##.##.', '.##.##.##.', '##########'],
  mais: ['..........', '..........', '..........', '..........', '.##.##.##.', '.##.##.##.', '..........', '..........', '..........', '..........'],
};

const ROTULO_ABA: Record<AbaId, Chave> = {
  escritorio: 'jogo.aba.escritorio',
  agentes: 'jogo.aba.agentes',
  entregas: 'jogo.aba.entregas',
  dia: 'jogo.aba.dia',
  mais: 'jogo.aba.mais',
};

/** Um painel que vira sheet: como achar o elemento, se está aberto e como fechar (pelo botão dele). */
interface Sheet {
  id: string;
  elemento: () => HTMLElement | null;
  aberta: () => boolean;
  fecha: () => void;
  /** Mostra o fundo escurecido tocável. Janelas com overlay próprio (Meu dia, Guia) não precisam. */
  fundo: boolean;
}

let montado = false;
let app: HTMLElement | undefined;
let hud: HTMLElement | undefined;
let abas: HTMLElement | undefined;
let fundo: HTMLElement | undefined;
/** Quando o fundo apareceu (para ignorar o clique fantasma do toque que abriu a sheet). */
let fundoDesde = 0;
const FANTASMA_MS = 450;
let folhaMais: HTMLElement | undefined;
let folhaConsumo: HTMLElement | undefined;
let folhaPush: HTMLElement | undefined;
let folhaSons: HTMLElement | undefined;
let observador: MutationObserver | undefined;
/** Estado das sheets abertas (as fechadas não entram no mapa). */
const estados = new Map<string, Folha>();
/** Último snapshot recebido: o HUD é refeito quando o idioma troca. */
let ultimo: { snapshot: OfficeSnapshot; conectado: boolean; precisam: number } | undefined;
let arrasto: { id: string; el: HTMLElement; y0: number; t0: number; estado0: Folha; altura0: number; dy: number } | undefined;

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
const visivel = (el: HTMLElement | null): boolean => !!el && !el.hidden;
// Escrever só quando muda: o observer vê qualquer atribuição (até a mesma) e o sincroniza se realimentaria
const esconde = (el: HTMLElement | null | undefined): void => {
  if (el && !el.hidden) el.hidden = true;
};
const alterna = (el: HTMLElement | null | undefined): void => {
  if (el) el.hidden = !el.hidden;
};
const clica = (sel: string, raiz: ParentNode | null): void => {
  raiz?.querySelector<HTMLElement>(sel)?.click();
};
const dispara = (painel: string): void => {
  window.dispatchEvent(new CustomEvent('agzos:painel', { detail: painel }));
};

/** As sheets, na ordem em que a tela as trata. Painéis que já existem: só ganham alça e altura. */
const SHEETS: Sheet[] = [
  { id: 'agentes', elemento: () => $('#lateral'), aberta: () => visivel($('#lateral')), fecha: () => esconde($('#lateral')), fundo: true },
  { id: 'agente', elemento: () => $('#detalhe'), aberta: () => visivel($('#detalhe')), fecha: () => clica('[data-close]', $('#detalhe')), fundo: true },
  { id: 'entregas', elemento: () => $('.entregas'), aberta: () => visivel($('.entregas')), fecha: () => clica('.entregas-fechar', $('.entregas')), fundo: true },
  { id: 'historico', elemento: () => $('.hist'), aberta: () => visivel($('.hist')), fecha: () => clica('.hist-fechar', $('.hist')), fundo: true },
  { id: 'dia', elemento: () => $('.mdia-caixa'), aberta: () => !!$('.mdia'), fecha: () => clica('.mdia-fechar', $('.mdia')), fundo: false },
  { id: 'guia', elemento: () => $('.guia-caixa'), aberta: () => visivel($('.guia')), fecha: () => clica('[data-guia-fechar]', $('.guia')), fundo: false },
  // o timelapse (B21) é um player que o main monta e desmonta: aqui vira bottom sheet, como os outros painéis
  { id: 'timelapse', elemento: () => $('.timelapse-player'), aberta: () => !!$('.timelapse-player'), fecha: () => clica('.timelapse-fechar', $('.timelapse-player')), fundo: true },
  { id: 'consumo', elemento: () => folhaConsumo ?? null, aberta: () => visivel(folhaConsumo ?? null), fecha: () => esconde(folhaConsumo), fundo: true },
  { id: 'mais', elemento: () => folhaMais ?? null, aberta: () => visivel(folhaMais ?? null), fecha: () => esconde(folhaMais), fundo: true },
  { id: 'push', elemento: () => folhaPush ?? null, aberta: () => visivel(folhaPush ?? null), fecha: () => esconde(folhaPush), fundo: true },
  { id: 'sons', elemento: () => folhaSons ?? null, aberta: () => visivel(folhaSons ?? null), fecha: () => esconde(folhaSons), fundo: true },
];

const sheetPorId = (id: string): Sheet | undefined => SHEETS.find((s) => s.id === id);

/** Liga a versão de celular à largura da tela: monta ao entrar e desmonta ao voltar para o desktop. */
export function montaMobile(): void {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', () => (mq.matches ? monta() : desmonta()));
  ouvirBarra(atualizaAbas);
  onLangChange(() => {
    if (montado) reTraduz();
  });
  if (mq.matches) monta();
}

function monta(): void {
  if (montado) return;
  app = document.getElementById('app') ?? undefined;
  if (!app) return;
  montado = true;

  hud = document.createElement('header');
  hud.className = 'hud-jogo';
  hud.addEventListener('click', (evento) => {
    if ((evento.target as HTMLElement).closest('[data-hud="anel"]')) alterna(folhaConsumo);
  });
  app.append(hud);

  abas = document.createElement('nav');
  abas.className = 'abas-jogo';
  abas.innerHTML = (Object.keys(ROTULO_ABA) as AbaId[])
    .map((id) => `<button type="button" data-aba="${id}" aria-pressed="false"><span class="aba-ico">${pixelSvg(GRADE[id])}</span><span class="aba-rotulo"></span><i class="selo-aba" hidden></i></button>`)
    .join('');
  abas.addEventListener('click', (evento) => {
    const id = (evento.target as HTMLElement).closest<HTMLElement>('[data-aba]')?.dataset.aba;
    if (id) aciona(id as AbaId);
  });
  app.append(abas);

  fundo = document.createElement('div');
  fundo.className = 'folha-fundo';
  fundo.hidden = true;
  // o toque que abriu a sheet gera um "clique fantasma" logo depois, no mesmo ponto: ele cairia no
  // fundo recém-mostrado e fecharia a sheet na hora. Toques no fundo só valem depois de FANTASMA_MS.
  fundo.addEventListener('click', () => {
    if (performance.now() - fundoDesde < FANTASMA_MS) return;
    SHEETS.find((s) => s.fundo && s.aberta())?.fecha();
  });
  // dentro do palco (main): a lateral e o painel do agente ficam no mesmo contexto de empilhamento,
  // então o fundo cobre o campus e fica abaixo das sheets
  (document.querySelector('main') ?? app).append(fundo);

  folhaMais = montaFolhaPropria('folha-mais');
  folhaConsumo = montaFolhaPropria('folha-consumo');
  folhaPush = montaFolhaPropria('folha-push');
  folhaPush.querySelector<HTMLElement>('.folha-corpo')?.classList.add('folha-push-corpo');
  folhaSons = montaFolhaPropria('folha-sons');
  folhaSons.querySelector<HTMLElement>('.folha-corpo')?.classList.add('folha-sons-corpo');
  folhaConsumo.querySelector<HTMLElement>('.folha-corpo')?.classList.add('folha-contas');

  // a lateral de agentes começa fechada: no celular ela é uma sheet aberta pela aba Agentes
  esconde($('#lateral'));

  observador = new MutationObserver(sincroniza);
  observador.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
  document.addEventListener('pointerdown', aoBaixar);
  document.addEventListener('pointermove', aoMover);
  document.addEventListener('pointerup', aoSoltar);
  document.addEventListener('pointercancel', aoSoltar);
  document.addEventListener('keydown', aoTecla);
  window.addEventListener('resize', mediFolhas);
  document.addEventListener('click', aoClicarDiaFora);

  reTraduz();
  mediFolhas();
  sincroniza();
  // o layout mudou sem a janela mudar de tamanho: o campus precisa se medir de novo
  window.dispatchEvent(new Event('resize'));
}

function desmonta(): void {
  if (!montado) return;
  montado = false;
  observador?.disconnect();
  observador = undefined;
  document.removeEventListener('pointerdown', aoBaixar);
  document.removeEventListener('pointermove', aoMover);
  document.removeEventListener('pointerup', aoSoltar);
  document.removeEventListener('pointercancel', aoSoltar);
  document.removeEventListener('keydown', aoTecla);
  window.removeEventListener('resize', mediFolhas);
  document.removeEventListener('click', aoClicarDiaFora);
  hud?.remove();
  abas?.remove();
  fundo?.remove();
  folhaMais?.remove();
  folhaConsumo?.remove();
  folhaPush?.remove();
  folhaSons?.remove();
  for (const el of document.querySelectorAll<HTMLElement>('.folha')) {
    el.classList.remove('folha', 'arrastando');
    delete el.dataset.sheet;
    delete el.dataset.folhaId;
    el.style.height = '';
    el.querySelector(':scope > .folha-alca')?.remove();
  }
  const lateral = $('#lateral');
  if (lateral) lateral.hidden = false;
  estados.clear();
  arrasto = undefined;
  hud = abas = fundo = folhaMais = folhaConsumo = folhaPush = folhaSons = undefined;
  window.dispatchEvent(new Event('resize'));
}

function montaFolhaPropria(id: string): HTMLElement {
  const el = document.createElement('section');
  el.id = id;
  el.className = 'folha-propria';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.innerHTML = `<header class="folha-cab"><b></b><button type="button" class="folha-fechar" aria-label="${t('acao.fechar')}">✕</button></header><div class="folha-corpo"></div>`;
  el.addEventListener('click', (evento) => {
    const alvo = evento.target as HTMLElement;
    if (alvo.closest('.folha-fechar')) return esconde(el);
    const acao = alvo.closest<HTMLElement>('[data-acao]')?.dataset.acao;
    if (acao) executaMais(acao);
    const lang = alvo.closest<HTMLElement>('[data-lang]')?.dataset.lang;
    if (lang) trocaIdioma(lang);
  });
  app?.append(el);
  return el;
}

// ------------------------------------------------------------------ HUD

const SEM_ANEL = '—';

function hudCasca(): string {
  return `<span class="hud-logo" aria-hidden="true">A</span>
    <span class="hud-vivo"><i class="hud-ponto" aria-hidden="true"></i><span data-hud="vivo"></span></span>
    <span class="hud-numeros">
      <span class="hud-num"><b data-hud="agentes">0</b><small>${t('num.agentes')}</small></span>
      <span class="hud-num verde"><b data-hud="trabalhando">0</b><small>${t('num.trabalhando')}</small></span>
      <span class="hud-num alerta" data-hud="precisam-caixa"><b data-hud="precisam">0</b><small>${t('num.precisam')}</small></span>
    </span>
    <button type="button" class="hud-anel sem" data-hud="anel" aria-label="${t('jogo.consumo')}" aria-haspopup="dialog">
      <svg viewBox="0 0 36 36" aria-hidden="true"><circle class="trilho" cx="18" cy="18" r="15.9" pathLength="100"/><circle class="arco" cx="18" cy="18" r="15.9" pathLength="100" stroke-dasharray="0 100"/></svg>
      <b data-hud="pct">${SEM_ANEL}</b>
    </button>
    <p class="hud-ultima"><span class="hud-rotulo">${t('jogo.ultima')}</span> <span data-hud="ultima"></span></p>`;
}

/**
 * Atualiza o HUD e o painel de consumo com o snapshot atual. O main chama a cada renderização do
 * topo; sem a versão de celular montada, só guarda o último valor.
 */
export function atualizaHud(snapshot: OfficeSnapshot, conectado: boolean, precisam: number): void {
  ultimo = { snapshot, conectado, precisam };
  if (!montado || !hud) return;
  const casa = hud;
  const campo = (chave: string) => casa.querySelector<HTMLElement>(`[data-hud="${chave}"]`);
  const escreve = (chave: string, valor: string) => {
    const el = campo(chave);
    if (el && el.textContent !== valor) el.textContent = valor;
  };
  escreve('vivo', conectado ? t('topo.ao_vivo') : t('topo.reconectando'));
  casa.classList.toggle('off', !conectado);
  const agentes = snapshot.agents;
  escreve('agentes', String(agentes.length));
  escreve('trabalhando', String(agentes.filter((a) => a.status === 'working' || a.status === 'thinking').length));
  escreve('precisam', String(precisam));
  campo('precisam-caixa')?.classList.toggle('ativo', precisam > 0);

  // o número do anel vem do mesmo cálculo de maiorConsumo5h; o estado (velho ou sem cota) é da CLI que o deu
  const anelDoConsumo = consumoDoAnel(snapshot.providers, Date.now());
  const pct = anelDoConsumo.pct;
  const anel = campo('anel');
  if (anel) {
    const estado = pct === undefined ? '' : ` ${nivelConsumo(pct)}${anelDoConsumo.desatualizado ? ' desatualizado' : ''}${anelDoConsumo.semCota ? ' semcota' : ''}`;
    anel.className = `hud-anel${pct === undefined ? ' sem' : estado}`;
  }
  casa.querySelector<SVGCircleElement>('.arco')?.setAttribute('stroke-dasharray', `${pct ?? 0} 100`);
  escreve('pct', anelDoConsumo.semCota ? t('stats.sem_cota') : pct === undefined ? SEM_ANEL : `${pct}%`);

  const porId = new Map(agentes.map((a) => [a.id, a]));
  const ultima = snapshot.activities.filter((a) => porId.has(a.agentId)).slice(-1)[0];
  // a linha de atividade desliza quando muda (a classe novo volta a zero a cada troca)
  const linha = campo('ultima');
  const texto = ultima ? `${porId.get(ultima.agentId)?.name ?? ''} · ${tActivity(ultima.text)}` : t('jogo.sem_atividade');
  if (linha && linha.textContent !== texto) {
    linha.textContent = texto;
    linha.classList.remove('novo');
    void linha.offsetWidth;
    linha.classList.add('novo');
  }

  // o painel de consumo copia o bloco de contas do topo, que o main já redesenha
  const corpo = folhaConsumo?.querySelector<HTMLElement>('.folha-corpo');
  const contas = $('#contas');
  if (corpo && contas) {
    const html = contas.children.length ? contas.innerHTML : `<p class="folha-vazio">${t('jogo.consumo_vazio')}</p>`;
    if (corpo.innerHTML !== html) corpo.innerHTML = html;
  }
  atualizaAbas();
}

// ------------------------------------------------------------------ abas

/** Ação de uma aba. Escritório fecha as sheets; os outros abrem ou fecham o painel dele. */
function aciona(id: AbaId): void {
  if (id === 'escritorio') {
    for (const s of SHEETS) if (s.aberta()) s.fecha();
  } else if (id === 'agentes') {
    alterna($('#lateral'));
  } else if (id === 'entregas' || id === 'dia') {
    // os painéis já alternam sozinhos ao receber o evento (o mesmo que a barra manda)
    dispara(id);
  } else {
    alterna(folhaMais);
    if (folhaMais && !folhaMais.hidden) preencheMais();
  }
}

function atualizaAbas(): void {
  if (!abas) return;
  const aberta = (id: string) => sheetPorId(id)?.aberta() ?? false;
  const marca: Record<AbaId, boolean> = {
    escritorio: !SHEETS.some((s) => s.aberta()),
    agentes: aberta('agentes'),
    entregas: aberta('entregas'),
    dia: aberta('dia'),
    mais: aberta('mais') || aberta('push'),
  };
  for (const id of Object.keys(marca) as AbaId[]) {
    const botao = abas.querySelector<HTMLElement>(`[data-aba="${id}"]`);
    if (!botao) continue;
    botao.setAttribute('aria-pressed', String(marca[id]));
    botao.classList.toggle('ativa', marca[id]);
  }
  // o mesmo selo de Entregas da barra; e o Agentes acende quando alguém precisa de você
  const selo = abas.querySelector<HTMLElement>('[data-aba="entregas"] .selo-aba');
  if (selo) {
    const texto = textoSelo(ferramentaDaBarra('entregas')?.badge?.() ?? 0);
    if (selo.textContent !== texto) selo.textContent = texto;
    if (selo.hidden !== (texto === '')) selo.hidden = texto === '';
  }
  abas.querySelector<HTMLElement>('[data-aba="agentes"]')?.classList.toggle('alerta', (ultimo?.precisam ?? 0) > 0);
}

// ------------------------------------------------------------------ notificações (B29-M5)

/** Textos do painel de notificações (src/web/push.ts), no idioma atual. */
function textosPush(): TextosPush {
  return {
    ativar: t('push.ativar'),
    desativar: t('push.desativar'),
    testar: t('push.testar'),
    testeOk: t('push.teste_ok'),
    testeSemAparelho: t('push.teste_sem_aparelho'),
    testeFalhou: t('push.teste_falhou'),
    ativo: t('push.ativo'),
    inativo: t('push.inativo'),
    semSuporte: t('push.sem_suporte'),
    ios: t('push.ios'),
    negada: t('push.negada'),
    falhou: t('push.falhou'),
    quando: t('push.quando'),
    precisa: t('push.precisa'),
    concluiu: t('push.concluiu'),
    aparelhos: t('push.aparelhos'),
    nenhum: t('push.nenhum'),
    remover: t('push.remover'),
    este: t('push.este'),
    salvo: t('push.salvo'),
  };
}

/** O item Sons do Mais (5.2): o painel de sons abre numa folha própria, no lugar do Mais. */
function abreSons(): void {
  esconde(folhaMais);
  if (!folhaSons) return;
  const cab = folhaSons.querySelector<HTMLElement>('.folha-cab b');
  if (cab) cab.textContent = t('sons.titulo');
  folhaSons.querySelector<HTMLElement>('.folha-corpo')?.replaceChildren(painelSons().el);
  folhaSons.hidden = false;
}

/** O item Notificações do Mais: a sheet do painel abre no lugar do Mais. */
function abrePush(): void {
  esconde(folhaMais);
  if (!folhaPush) return;
  const cab = folhaPush.querySelector<HTMLElement>('.folha-cab b');
  if (cab) cab.textContent = t('push.item');
  const corpo = folhaPush.querySelector<HTMLElement>('.folha-corpo');
  if (corpo) {
    const painel = document.createElement('div');
    painel.className = 'push-painel';
    corpo.replaceChildren(painel);
    void montaPainelPush(painel, textosPush());
  }
  folhaPush.hidden = false;
}

// ------------------------------------------------------------------ menu "Mais"

const NOMES_IDIOMA: Record<(typeof IDIOMAS)[number], string> = { pt: 'Português', en: 'English', es: 'Español', fr: 'Français' };

/** Itens do Mais: as ferramentas da barra (mesma ação e ícone) e os botões do topo. */
function preencheMais(): void {
  const corpo = folhaMais?.querySelector<HTMLElement>('.folha-corpo');
  if (!corpo) return;
  const itens = ['historico', 'timelapse', 'terminal', 'tema', 'ajuda'].map((id) => {
    const f = ferramentaDaBarra(id);
    return `<button type="button" data-acao="${id}"><span class="mais-ico">${f?.icon ?? ''}</span><span>${f?.label ?? id}</span></button>`;
  });
  itens.push(`<button type="button" data-acao="ajustes"><span class="mais-ico"></span><span>${t('topo.ajustes')}</span></button>`);
  itens.push(`<button type="button" data-acao="ponte"><span class="mais-ico"></span><span>${t('topo.ponte')}</span></button>`);
  itens.push(`<button type="button" data-acao="notificacoes"><span class="mais-ico"></span><span>${t('push.item')}</span></button>`);
  itens.push(`<button type="button" data-acao="sons"><span class="mais-ico"></span><span>${t('sons.titulo')}</span></button>`);
  const atual = $<HTMLSelectElement>('#idioma')?.value ?? getLang();
  const idiomas = IDIOMAS.map((lang) => `<button type="button" data-lang="${lang}" aria-pressed="${lang === atual}">${NOMES_IDIOMA[lang]}</button>`).join('');
  corpo.innerHTML = `<div class="mais-lista">${itens.join('')}</div>
    <div class="mais-idioma"><span>${t('topo.idioma')}</span><div class="mais-langs">${idiomas}</div></div>
    <button type="button" class="mais-sair" data-acao="sair">${t('topo.sair')}</button>`;
}

/** Ações que o Mais repassa: as da barra e as do topo (Ajustes, Ponte e Sair são os botões do topo). */
function executaMais(acao: string): void {
  if (acao === 'ajustes') return clica('#btn-ajustes', document);
  if (acao === 'ponte') return clica('#btn-ponte', document);
  if (acao === 'sair') return clica('#logout', document);
  if (acao === 'notificacoes') return abrePush();
  if (acao === 'sons') return abreSons();
  esconde(folhaMais);
  ferramentaDaBarra(acao)?.onClick();
  refreshTools();
}

/** Troca o idioma pelo próprio seletor do topo (o main escuta o change dele). */
function trocaIdioma(lang: string): void {
  const select = $<HTMLSelectElement>('#idioma');
  if (!select) return;
  select.value = lang;
  select.dispatchEvent(new Event('change'));
}

// ------------------------------------------------------------------ sheets: estado, alça e arrasto

/** Alturas (px) de meia e de altura cheia: a meia é metade da tela; a cheia vai do fim do HUD até a barra de abas. */
function medidas(): Medidas {
  const topo = hud ? hud.getBoundingClientRect().bottom : 0;
  const base = abas ? abas.getBoundingClientRect().height : 0;
  return { meia: Math.round(window.innerHeight * FRACAO_MEIA), cheia: Math.max(0, Math.round(window.innerHeight - topo - base)) };
}

/** Passa as alturas para o CSS (variáveis --folha-meia e --folha-cheia). */
function mediFolhas(): void {
  const m = medidas();
  document.documentElement.style.setProperty('--folha-meia', `${m.meia}px`);
  document.documentElement.style.setProperty('--folha-cheia', `${m.cheia}px`);
}

/** Alça no topo da sheet (o arrasto começa nela). Refeita se o painel for redesenhado por dentro. */
function garanteAlca(el: HTMLElement): void {
  if (el.querySelector(':scope > .folha-alca')) return;
  const alca = document.createElement('div');
  alca.className = 'folha-alca';
  alca.setAttribute('aria-hidden', 'true');
  alca.title = t('jogo.arrastar');
  alca.innerHTML = '<span></span>';
  el.prepend(alca);
}

/** Sincroniza o estado das sheets com o DOM: quem abriu (e fecha os outros), quem fechou, a alça e o fundo. */
function sincroniza(): void {
  if (!montado) return;
  for (const s of SHEETS) {
    const el = s.elemento();
    if (el) {
      el.classList.add('folha');
      el.dataset.folhaId = s.id;
      garanteAlca(el);
    }
    const aberta = s.aberta();
    const antes = estados.get(s.id);
    if (aberta && !antes) {
      estados.set(s.id, 'meia');
      // na tela estreita, só uma sheet fica aberta por vez
      for (const outra of SHEETS) if (outra.id !== s.id && outra.aberta()) outra.fecha();
    } else if (!aberta && antes) {
      estados.delete(s.id);
    }
    const agora = estados.get(s.id);
    if (el && agora && el.dataset.sheet !== agora) el.dataset.sheet = agora;
    if (el && !agora && el.dataset.sheet) delete el.dataset.sheet;
  }
  const fundoAberto = SHEETS.some((s) => s.fundo && s.aberta());
  if (fundo && fundo.hidden !== !fundoAberto) {
    if (fundoAberto) fundoDesde = performance.now();
    fundo.hidden = !fundoAberto;
  }
  atualizaAbas();
}

function aoBaixar(evento: PointerEvent): void {
  const alca = (evento.target as Element | null)?.closest<HTMLElement>('.folha-alca');
  const el = alca?.parentElement;
  const id = el?.dataset.folhaId;
  if (!alca || !el || !id) return;
  evento.preventDefault();
  arrasto = { id, el, y0: evento.clientY, t0: performance.now(), estado0: estados.get(id) ?? 'meia', altura0: el.getBoundingClientRect().height, dy: 0 };
  el.classList.add('arrastando');
  alca.setPointerCapture?.(evento.pointerId);
}

function aoMover(evento: PointerEvent): void {
  if (!arrasto) return;
  arrasto.dy = evento.clientY - arrasto.y0;
  // a altura segue o dedo, sem passar do zero nem da altura cheia
  const altura = Math.min(medidas().cheia, Math.max(0, arrasto.altura0 - arrasto.dy));
  arrasto.el.style.height = `${altura}px`;
}

function aoSoltar(): void {
  if (!arrasto) return;
  const a = arrasto;
  arrasto = undefined;
  a.el.classList.remove('arrastando');
  a.el.style.height = '';
  const vel = a.dy / Math.max(1, performance.now() - a.t0);
  const novo = estadoAposArrasto(a.estado0, a.dy, vel, medidas());
  if (novo === 'fechada') {
    sheetPorId(a.id)?.fecha();
    return;
  }
  estados.set(a.id, novo);
  a.el.dataset.sheet = novo;
}

/** Tocar no fundo escuro do Meu dia (o próprio overlay, fora da caixa) fecha o painel. */
function aoClicarDiaFora(evento: MouseEvent): void {
  const overlay = $('.mdia');
  if (overlay && evento.target === overlay) clica('.mdia-fechar', overlay);
}

/** Esc fecha as sheets que não têm o Esc próprio (Entregas, Histórico, Guia e Meu dia já tratam o deles). */
function aoTecla(evento: KeyboardEvent): void {
  if (evento.key !== 'Escape') return;
  SHEETS.find((s) => ['mais', 'consumo', 'agentes'].includes(s.id) && s.aberta())?.fecha();
}

// ------------------------------------------------------------------ textos

/** Refaz os textos do HUD, das abas e dos painéis próprios ao trocar o idioma. */
function reTraduz(): void {
  if (!montado) return;
  if (hud) hud.innerHTML = hudCasca();
  if (abas) {
    for (const id of Object.keys(ROTULO_ABA) as AbaId[]) {
      const rotulo = t(ROTULO_ABA[id]);
      const botao = abas.querySelector<HTMLElement>(`[data-aba="${id}"]`);
      if (!botao) continue;
      botao.setAttribute('aria-label', rotulo);
      const texto = botao.querySelector<HTMLElement>('.aba-rotulo');
      if (texto) texto.textContent = rotulo;
    }
    abas.setAttribute('aria-label', t('jogo.abas'));
  }
  const painelTitulo: Array<[HTMLElement | undefined, string]> = [
    [folhaMais, t('jogo.aba.mais')],
    [folhaConsumo, t('jogo.consumo')],
    [folhaPush, t('push.item')],
  ];
  for (const [el, titulo] of painelTitulo) {
    if (!el) continue;
    el.setAttribute('aria-label', titulo);
    const cab = el.querySelector<HTMLElement>('.folha-cab b');
    if (cab) cab.textContent = titulo;
    el.querySelector<HTMLElement>('.folha-fechar')?.setAttribute('aria-label', t('acao.fechar'));
  }
  for (const alca of document.querySelectorAll<HTMLElement>('.folha-alca')) alca.title = t('jogo.arrastar');
  if (folhaMais && !folhaMais.hidden) preencheMais();
  if (folhaPush && !folhaPush.hidden) abrePush();
  if (ultimo) atualizaHud(ultimo.snapshot, ultimo.conectado, ultimo.precisam);
}
